import React, { useState } from "react";
import { Rate, Upload, Image } from "antd";
import "bootstrap/dist/css/bootstrap.min.css";
import "./ReviewModal.css";
import { PlusOutlined } from "@ant-design/icons";
import api from "../components/api";
import { trackProductReview, trackPhotoReview } from "./xpUtils";


const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const ReviewModal = ({
  isOpen,
  onClose,
  product,
  currentValue,
  setCurrentValue,
  onSubmit,
  ratingError,
  setRatingError
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [reviewText, setReviewText] = useState("");
  const [submitError, setSubmitError] = useState("");

  const [fileList, setFileList] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");



  const handleSubmit = async () => {
      // Clear any existing XP before starting
      console.log("Clearing earnedXP");
      localStorage.removeItem('earnedXP');
    //Check if rating is selected
    if (currentValue === 0) {
      setSubmitError("Please select a star rating before submitting")
      return;
    }

    //Clear any previous errors
    setSubmitError("");

    if (currentValue > 0 && reviewText.trim()) {
      try {
        // Create FormData to handle both text and files
        const formData = new FormData();

        // Append text fields
        formData.append("rate", currentValue);
        formData.append("text", reviewText);

        // Append each file from fileList to FormData
        fileList.forEach((file) => {
          if (file.originFileObj) {
            formData.append("photos", file.originFileObj); // Append the actual file object
          }
        });

        // Post review to the backend
        const response = await api.patch(
          `/products/${product.id}/review`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data", // Ensure it's multipart/form-data for file uploads
            },
          }
        );

        // Check if the response is successful
        if (response.status === 200 || response.status === 201) {
          // Award XP for writing a product review
          console.log("Review successful - tracking XP for product review");
          trackProductReview(); // This gives 50 XP
          console.log("After product review, localStorage value:", localStorage.getItem('earnedXP'));
          
          // If photos were included, award additional XP
          if (fileList.length > 0) {
            console.log("Photos included - tracking additional XP (20 XP total for having photos)");
            trackPhotoReview();
            console.log("After photo review, localStorage value:", localStorage.getItem('earnedXP'));
          }
        
          setIsSubmitted(true);
        } else {
          // Handle the error (optional: display an error message)
          console.error("Error submitting review:", response.statusText);
          setSubmitError("Failed to submit review. Please try again");
        }
      } catch (error) {
        console.error("Error during API call:", error);
        setSubmitError("Failed to submit review. Please try again");
      }
    } else if (currentValue > 0 && !reviewText.trim()) {
      //If rating is selected but review text is empty
      setSubmitError("Please write a review before submitting")
    }
  };

  const handleClose = () => {
    // Check if we need to trigger the XP popup
    const xpValue = localStorage.getItem('earnedXP');
    
    // Close the modal
    onClose();
    
    // Refresh the product details
    onSubmit();
    
    // Reset local state
    setIsSubmitted(false);
    
    // If there's XP to show, force a check by dispatching a custom event
    if (xpValue) {
      console.log("handleClose - Triggering XP popup check with value:", xpValue);
      
      // Dispatch a custom event that XPContext can listen for
      window.dispatchEvent(new CustomEvent('check-xp-earned', { 
        detail: { xp: xpValue } 
      }));
    }
  };

  // phote preview
  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const handleChange = async ({ fileList: newFileList }) => {
    const processedFileList = await Promise.all(
      newFileList.map(async (file) => {
        if (!file.url && !file.preview) {
          file.preview = await getBase64(file.originFileObj);
        }
        return { ...file, status: "done" };
      })
    );

    setFileList(processedFileList);
  };

  if (!product) {
    return null;
  }

  return (
    <>
      {isOpen && (
        <div
          className={`modal fade model-index ${isOpen ? "show d-block" : ""}`}
          tabIndex="-1"
        >
          <div className="modal-dialog"> 
            <div
              className={`modal-content ${
                !isSubmitted ? "review-modal" : "submit-confirmation-modal"
              }`}
            >
              {!isSubmitted ? (
                <>
                  {/* Write review modal */}
                  <div className="modal-header review-header border-bottom-0"> 
                    <div className="header-content"> 
                      <img
                        src={product.image}
                        alt={product.name}
                        className="review-product-image"
                      />
                      <div className="product-details">
                        <h5 className="modal-title mb-3">{product.name}</h5>
                        <Rate
                          onChange={(value) => {
                            setCurrentValue(value);
                            if (submitError) {
                              setSubmitError("");
                            }
                            if (setRatingError && typeof setRatingError === 'function') {
                              setRatingError("");
                            }
                          }}
                          value={currentValue}
                          style={{ fontSize: "1.5rem" }}
                        />
                        {(ratingError || submitError) && (
                          <div className="review-error-message mt-2">
                            {ratingError || submitError}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={onClose}
                    ></button>
                  </div>
  
                  <div className="modal-body">
                    <textarea
                      className="form-control review-text-area w-100 mb-3"
                      rows="6"
                      placeholder="Please write a review here."
                      value={reviewText}
                      onChange={(e) => {
                        setReviewText(e.target.value);
                        if (submitError && submitError.includes ("write a review")) {
                          setSubmitError("");
                        }
                      }}
                    />
  
                    <div className="upload-photo w-100 p-3">
                      <Upload
                        listType="picture-card"
                        fileList={fileList}
                        maxCount={10}
                        accept="image/*"
                        onPreview={handlePreview}
                        onChange={handleChange}
                      >
                        {fileList.length >= 10 ? null : (
                          <button
                            className="btn-upload d-flex flex-column align-items-center border-0 bg-transparent text-dark p-0"
                            type="button"
                          >
                            <PlusOutlined />
                            <div className="mt-2">Upload</div>
                          </button>
                        )}
                      </Upload>
  
                      {previewImage && (
                        <Image
                          wrapperStyle={{
                            display: "none",
                          }}
                          preview={{
                            visible: previewOpen,
                            onVisibleChange: (visible) =>
                              setPreviewOpen(visible),
                            afterOpenChange: (visible) =>
                              !visible && setPreviewImage(""),
                          }}
                          src={previewImage}
                        />
                      )}
                    </div>
                  </div>
  
                  <div className="modal-footer justify-content-center border-top-0">
                    <button
                      type="button"
                      className="btn btn-primary btn-submit-close"
                      onClick={handleSubmit}
                    >
                      Submit
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* Submit confirmation modal */}
                  <div className="modal-body d-flex flex-column align-items-center">
                    <img
                      src={`/images/icons/Post Submitted.svg`}
                      alt="Review Submitted"
                      className="mt-3 mb-3"
                    />
                    <h5 className="confirmation-title fw-bold fs-4 mb-3">
                      Review Submitted!
                    </h5>
                  </div>
                  <div className="modal-footer justify-content-center border-top-0">
                    <button
                      type="button"
                      className="btn btn-primary btn-submit-close"
                      onClick={handleClose}
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
  
      {isOpen && <div className="modal-backdrop fade show"></div>}
    </>
  );
};

export default ReviewModal;


